import { useState, useEffect, useCallback } from 'react';
import { additionalAPI, splitContactUsers, ContactUser } from '../services/additional-api';
import { useAuth } from '../contexts/AuthContext';

interface UseContactUsersResult {
  contactUsers: ContactUser[];
  retiredUsers: ContactUser[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export function useContactUsers(): UseContactUsersResult {
  const { user } = useAuth();
  const [contactUsers, setContactUsers] = useState<ContactUser[]>([]);
  const [retiredUsers, setRetiredUsers] = useState<ContactUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // systemdataflag=true のログインユーザーを先頭に追加する
  const injectSystemUser = useCallback((users: ContactUser[]): ContactUser[] => {
    if (!user?.systemdataflag || !user.userno) return users;
    if (users.some(u => u.userno === user.userno)) return users;
    return [
      {
        userno: user.userno,
        username: user.username,
        deptno: user.deptno ?? 0,
        deptname: user.deptname ?? '',
        mailaddress: user.mailaddress ?? '',
      },
      ...users,
    ];
  }, [user]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // キャッシュ・TTL 判定・退職者の切り分けはすべて additionalAPI 側が持つ
      const res = await additionalAPI.getContactUsers();
      if (res.success && Array.isArray(res.data)) {
        const { active, retired } = splitContactUsers(res.data);
        setContactUsers(injectSystemUser(active));
        setRetiredUsers(retired);
      }
    } catch (error) {
      console.error('担当者一覧取得エラー:', error);
    } finally {
      setIsLoading(false);
    }
  }, [injectSystemUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  return { contactUsers, retiredUsers, isLoading, refetch: load };
}
